import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { I18nService } from '@localize/services/';
import { UrlCheckService } from '@services/url-check/url-check.service';
import includes from 'lodash/includes';
import { Observable, of } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';

import { FormEditorInputType } from '../../shared/components/forms/_models';
import { UserService } from '../user';

@Injectable({
	providedIn: 'root'
})
export class FormsService {
	// URL validator, enforces protocol
	public urlFormatValidator = Validators.pattern('(https?://)([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');

	constructor(private userService: UserService, private i18nService: I18nService, private urlCheckService: UrlCheckService) {}

	public getErrorMessage(control: AbstractControl, type: FormEditorInputType): string {
		switch (type) {
			case FormEditorInputType.email:
				if (control.hasError('email')) {
					return this.i18nService.get('Not a valid email', 'app.api.response.not-valid-email');
				} else if (control.hasError('loginExist')) {
					return this.i18nService.get('Already registered', 'app.prompt.already-registered');
				} else if (control.hasError('contactInvalid')) {
					return this.i18nService.get('No such user', 'app.api.response.non-existing-user');
				} else if (control.hasError('contactUnverified')) {
					return this.i18nService.get('No such user', 'app.api.response.unverified-user');
				} else if (control.hasError('emailExists')) {
					return this.i18nService.get('This email has already registered', 'app.prompt.email-exists');
				} else if (type === FormEditorInputType.email && control.hasError('checkingEmail')) {
					return this.i18nService.get('Validating email', 'app.action.validating-email');
				}
				break;
			case FormEditorInputType.number:
				if (control.hasError('min')) {
					return `${this.i18nService.get('cannot be less than', 'app.prompt.min-value')} ${control.errors.min.min}`;
				}
				break;
			case FormEditorInputType.url:
				if (control.hasError('pattern') || control.hasError('url')) {
					return this.i18nService.get('Not a valid URL', 'app.api.response.not-valid-url');
				} else if (control.hasError('urlMissingEncryption')) {
					return this.i18nService.get('Not a valid URL', 'app.api.response.url-missing-encryption');
				} else if (control.hasError('urlMissingProtocol')) {
					return this.i18nService.get('Not a valid URL', 'app.api.response.url-missing-protocol');
				} else if (control.hasError('urlNotReachable')) {
					return this.i18nService.get('Not reachable URL', 'app.api.response.not-reachable-url');
				} else if (control.hasError('checkingUrl')) {
					return this.i18nService.get('Validating URL', 'app.action.validating-url');
				}
				break;
			case FormEditorInputType.tel:
				if (control.hasError('phoneInvalid')) {
					return this.i18nService.get('Phone number formatted incorrectly, example: +61123456789', 'app.prompt.phone-is-invalid');
				}
				break;
			case FormEditorInputType.password:
				if (control.hasError('password')) {
					return this.i18nService.get(
						'minimum 8, a mix of uppercase, lowercase and number characters',
						'app.prompt.password-requirements'
					);
				} else if (control.hasError('passwordNotMatch')) {
					return this.i18nService.get('Passwords do not match', 'app.prompt.passwords-do-not-match');
				}
				break;
			case FormEditorInputType.text:
				if (control.hasError('appNameInvalid')) {
					return this.i18nService.get(
						'Name can only contain alphanumeric characters, periods, hyphens, underscores and spaces.',
						'app.prompt.app-name-is-invalid'
					);
				}
				break;
			case FormEditorInputType.text:
                if (control.hasError('uuidInvalid')) {
                    return this.i18nService.get(
                        'Invalid UUID.',
                        'app.prompt.uuid-is-invalid'
                    );
                }
                break;
		}

		// Catch all, these messages does aren't limited to a type
		if (control.hasError('required')) {
			return this.i18nService.get('This field is required', 'app.prompt.form-field-is-required');
		} else if (control.hasError('numerical')) {
			return this.i18nService.get('The value must be numeric', 'app.prompt.numerical');
		} else {
			return '';
		}
	}

	public contactValid(valids: any[] = []): AsyncValidatorFn {
		return (control: AbstractControl): Promise<{ [key: string]: any } | null> | Observable<{ [key: string]: any } | null> => {
			if (this.isEmptyInputValue(control.value)) {
				return of(null);
			} else if (includes(valids, control.value)) {
				return of(null);
			} else {
				return !control.valueChanges
					? of(null)
					: control.valueChanges.pipe(
							tap((_) => control.setErrors({ checkingEmail: true })),
							delay(800),
							debounceTime(800),
							distinctUntilChanged(),
							take(1),
							switchMap((_) =>
								this.userService.userExists(control.value).pipe(map((exists) => (exists ? null : { contactInvalid: true })))
							)
					  );
			}
		};
	}

	public emailAvailable(valids: any[] = []): AsyncValidatorFn {
		return (control: AbstractControl): Promise<{ [key: string]: any } | null> | Observable<{ [key: string]: any } | null> => {
			if (this.isEmptyInputValue(control.value)) {
				return of(null);
			} else if (includes(valids, control.value)) {
				return of(null);
			} else {
				return !control.valueChanges
					? of(null)
					: control.valueChanges.pipe(
							tap((_) => control.setErrors({ checkingEmail: true })),
							delay(800),
							debounceTime(800),
							distinctUntilChanged(),
							take(1),
							switchMap((_) =>
								this.userService.userExists(control.value).pipe(map((exists) => (exists ? { loginExist: true } : null)))
							)
					  );
			}
		};
	}

	public urlValid(valids: any[] = []): AsyncValidatorFn {
		return (control: AbstractControl): Promise<{ [key: string]: any } | null> | Observable<{ [key: string]: any } | null> => {
			if (this.isEmptyInputValue(control.value) || !control.value || !control.value.trim()) {
				return of(null);
			} else if (includes(valids, control.value)) {
				return of(null);
			} else {
				return !control.valueChanges
					? of(null)
					: control.valueChanges.pipe(
							tap((_) => control.setErrors({ checkingUrl: true })),
							delay(800),
							debounceTime(800),
							distinctUntilChanged(),
							take(1),
							switchMap((_) =>
								this.urlCheckService
									.invalid(control.value)
									.pipe(
										map((result) =>
											!result.valid ? { url: true } : !result.reachable ? { urlNotReachable: true } : null
										)
									)
							)
					  );
			}
		};
	}

	public validEmail(email: string): boolean {
		return /([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+/.test(
			email
		);
	}

	/**
	 * Will match control value with an adjacent control named 'password'. Ensure both controls
	 * sits within the same container and the comparing one is named 'password'.
	 */
	public confirmPasswordMatcher(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const passwordMatch = control.value === control.parent?.controls?.['password']?.value;

			return !passwordMatch ? { passwordNotMatch: { value: control.value } } : null;
		};
	}

	/**
	 * Validator to validate App Name format. Only allow alpha, numerical and white space values.
	 * Returns a validator that will set a appNameInvalid attr in form controls error object.
	 */
	public appNameValidator(): ValidatorFn {
		const regEx = new RegExp('^([A-Za-z0-9\\s-_.])+$');

		return (control: AbstractControl): { [key: string]: any } | null => {
			const appNameInvalid = !regEx.test(control.value);
			return appNameInvalid ? { appNameInvalid: { value: control.value } } : null;
		};
	}

	/**
	 * Validator to validate UUID. 
	 */
	public uuidValidator(): ValidatorFn {
        const regEx = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$');
            
        return (control: AbstractControl): { [key: string]: any } | null => {
            const uuidInvalid = !regEx.test(control.value);
            return uuidInvalid ? { uuidInvalid: { value: control.value } } : null;
        };                  
    }                       

	/**
	 * URL format regex validator. Will by default allow both HTTP and HTTPS URLs
	 * but can be set to enforce HTTPS.
	 */
	public urlValidator(forceEncryption = false, allowEmpty = false): ValidatorFn {
		const regEx = forceEncryption
			? new RegExp('(https://)([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w.-]*/?[\\S]*$')
			: new RegExp('(https?://)([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w.-]*/?[\\S]*$');

		const errorType = forceEncryption ? 'urlMissingEncryption' : 'urlMissingProtocol';

		return (control: AbstractControl): { [key: string]: any } | null => {
			if (allowEmpty && (!control.value || !control.value.trim())) {
				return null;
			}

			const invalidUrl = !regEx.test(control.value ? control.value?.trim() : control.value);

			return invalidUrl ? { [errorType]: { value: control.value } } : null;
		};
	}

	public phoneValidator(allowEmpty = false): ValidatorFn {
		const regEx = new RegExp('^\\+([0-9-])+$');

		return (control: AbstractControl): { [key: string]: any } | null => {
			const invalidPhone = !regEx.test(control.value);

			if (allowEmpty && (!control.value || !control.value.trim())) {
				return null;
			}

			return invalidPhone ? { phoneInvalid: { value: control.value } } : null;
		};
	}

	public emailValidator(allowEmpty = false): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const invalidEmail = control.value && control.value.trim() ? !this.validEmail(control.value) : true;

			if (allowEmpty && (!control.value || !control.value.trim())) {
				return null;
			}

			return invalidEmail ? { email: { value: true } } : null;
		};
	}

	public min(min: number, allowEmpty = false): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (allowEmpty && !Number.isFinite(control.value)) {
				return null;
			}

			return control.value < min ? { min: { min: min, actual: control.value } } : null;
		};
	}

	public minLength(minLength: number, allowEmpty = false): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (allowEmpty && (!control.value || !control.value.trim())) {
				return null;
			}

			return !control.value || !control.value.trim() || control.value.trim().length < minLength
				? { minLength: { value: true } }
				: null;
		};
	}

	public numerical(allowEmpty = false): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (allowEmpty && (!control.value || !control.value.trim())) {
				return null;
			}

			return !control.value || !control.value.trim() || !/^[0-9]+$/.test(control.value.trim())
				? { numerical: { value: true } }
				: null;
		};
	}

	public required(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			return !control.value || !control.value.trim() ? { required: { value: true } } : null;
		};
	}

	public shouldBeTrue(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			return !control.value ? { required: { value: true } } : null;
		};
	}

	private isEmptyInputValue(value: any): boolean {
		// we don't check for string here so it also works with arrays
		return value === null || (value && value.length === 0);
	}
}
